import { t } from 'i18next';
import _ from 'lodash';
import { sqScalingTemplateStore } from '@/core/core.stores';
import { validateFormula } from '@/formula/formula.utilities';
import {
  getExistingTableDefinition,
  setColumnIdToBeUsedAsTemplateInput,
  setDoTableReload,
  setIsApplyingTemplate,
  setSelectedColumns,
  setTemplateInputToSelect,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { CONTROL_PANE_DISPLAY, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  setColumnToEditId,
  setControlPane,
  setShowColumnProperties,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { addOrUpdateColumn } from '@/tableDefinitionEditor/tableGridColumns.utilities';
import { invalidateTableTemplate } from '@/tableDefinitionEditor/tableTemplate.queries';
import {
  findMappingForFeature,
  getTemplateApplicationKey,
  normalizeId,
} from '@/tableDefinitionEditor/templateTableApplication.utilities';
import { setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { TemplateColumnHeader } from '@/tools/models/TemplateColumnHeader.atom';
import { updateInputFeature } from '@/tools/models/editTemplate.utilities';
import { captureInputRestrictionType } from '@/tools/models/outputItemMetadata.utilities';
import {
  getTemplateInputFeatures,
  resetTemplate,
  setTemplate,
  setTemplateOutput,
} from '@/tools/models/template.actions';
import { fetchItemsForMetadata } from '@/tools/models/template.api';
import {
  TEMPLATE_TYPES,
  type TemplateDerivedFeature,
  type TemplateOutputSelectValue,
  isSystemInputFeature,
} from '@/tools/models/template.types';
import { getTemplateInstanceIdFromProps } from '@/tools/models/templateInstance.utilities';
import { computeRetiredOutputItemIds } from '@/tools/models/templateOutputStatus.utilities';
import { evictInstanceQuery } from '@/tools/models/useInstanceCompatibilityCheck.hook';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { addTrendItem } from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import { clearRetiredTemplatesProps, clearTemplateProps, getTemplateProps } from '@/trendData/trendTemplate.actions';
import { getDependencies } from '@/utilities/formula.utilities';
import { isForbidden } from '@/utilities/http.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { getNextName } from '@/utilities/utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import {
  OutputTypeEnum as DerivedFeatureOutputTypeEnum,
  type DerivedFeatureV1,
} from '@seeqdev/api/model/DerivedFeatureV1';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import { SeeqNames } from '@seeqdev/constants';
import type { QueryClient } from '@tanstack/react-query';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';
import type { ColumnDefinition, EditorType, ModelToSelect } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.molecule';
import type {
  CollectedInputsAndParents,
  DerivedFeatureWithColumnDefinition,
  ModelRowItem,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  OutputDependencyItem,
  OutputItemMetadataFields,
  SelectableOutput,
  TemplateInput,
  TemplateType,
  TemplateVersionMetadata,
} from '@/tools/models/template.types';
import type { ApplicationVersionInfo } from '@/tools/models/useTableTemplateVersionMetadata.hook';
import type { CurrentUser } from '@/workbench/workbench.store';
import type { ColDef, ColGroupDef } from '@seeqdev/ag/grid';
import type { GqlIndustryTemplatesQuery } from '@seeqdev/api/graphql/IndustryTemplates';
import type { GqlMyTemplatesQuery } from '@seeqdev/api/graphql/MyTemplates';
import type { GqlResetTemplateDraftMutation } from '@seeqdev/api/graphql/ResetTemplateDraft';
import type { GqlSharedTemplatesQuery } from '@seeqdev/api/graphql/SharedTemplates';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { InstanceOutputMappingV1 } from '@seeqdev/api/model/InstanceOutputMappingV1';
import type { InstanceOutputV1 } from '@seeqdev/api/model/InstanceOutputV1';
import type { ItemOutputV1 } from '@seeqdev/api/model/ItemOutputV1';
import type { ReferencedOutputItemV1 } from '@seeqdev/api/model/ReferencedOutputItemV1';
import type { TemplateApplicationMappingV1 } from '@seeqdev/api/model/TemplateApplicationMappingV1';
import type { TemplateDerivedColumnNamesInputV1 } from '@seeqdev/api/model/TemplateDerivedColumnNamesInputV1';
import type { TemplateInitialApplicationInputV1 } from '@seeqdev/api/model/TemplateInitialApplicationInputV1';
import type { TemplateReapplicationInputV1 } from '@seeqdev/api/model/TemplateReapplicationInputV1';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';
import type { TemplateTableApplicationsOutputV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/models';

export interface ItemOutputWithLaneAndAxis extends ItemOutputV1 {
  lane?: number;
  axisAlign?: string;
}

/**
 * Merges version/draft metadata into template items. Pure function --
 * takes a list of templates and a metadata map, returns templates with
 * `versionMetadata` attached where available.
 */
export const mergeVersionMetadata = <T extends { id: string }>(
  models: T[],
  metadataMap: Map<string, TemplateVersionMetadata>,
): T[] =>
  models.map((m) => {
    const metadata = metadataMap.get(m.id);
    return metadata ? { ...m, versionMetadata: metadata } : m;
  });

/**
 * Filters templates based on ownership determined by versionMetadata.
 * Only includes templates where the current user is (or is not) the original creator.
 * TODO: CRAB-60044 Replace with check for owner property after switching to use templates search endpoint
 *
 * @param templates - Array of templates to filter
 * @param currentUser - The current user
 * @param includeOwned - If true, returns templates owned by current user; if false, returns templates NOT owned by current user
 * @returns Filtered array of templates
 */
export const filterTemplatesByOwnership = <T extends { id: string; versionMetadata?: TemplateVersionMetadata }>(
  templates: T[],
  currentUser: CurrentUser | undefined,
  includeOwned: boolean,
): T[] => {
  if (currentUser?.name === undefined || currentUser?.isAdmin === undefined) {
    return templates;
  }

  return templates.filter((template) => {
    const versionMetadata = template.versionMetadata;
    if (!versionMetadata?.versions || versionMetadata.versions.length === 0) {
      // No version history - can't determine ownership, include it
      return true;
    }
    // versions are sorted newest first, so the last one is the original creator
    const firstVersion = versionMetadata.versions[versionMetadata.versions.length - 1];
    const isOwner = firstVersion.creatorName === currentUser.name;
    return includeOwned ? isOwner || currentUser.isAdmin : !isOwner;
  });
};

type ModelLibraryTemplate = {
  id: string;
  versionMetadata?: TemplateVersionMetadata;
  datasource?: { id?: string };
};

export type ModelLibraryTemplatePartition<T extends ModelLibraryTemplate> = {
  myTemplates: T[];
  sharedTemplatesNotOwned: T[];
  industryTemplates: T[];
  /** User-datasource shared templates not owned by the current user, before removing owned duplicates. */
  sharedTemplatesForTotalCount: T[];
};

/** Partitions workbook and shared templates into My / Shared / Industry lists for Model Library display. */
export const partitionModelLibraryTemplates = <T extends ModelLibraryTemplate>(
  templates: T[],
  sharedTemplates: T[],
  currentUser: CurrentUser | undefined,
): ModelLibraryTemplatePartition<T> => {
  const myTemplates = filterTemplatesByOwnership(templates, currentUser, true);
  const myTemplateIds = new Set(myTemplates.map((model) => model.id));

  const sharedUserDatasourceTemplates = sharedTemplates.filter(
    (template) => template.datasource?.id === SeeqNames.LocalDatasources.Templates.User.DatasourceId,
  );
  const sharedTemplatesForTotalCount = filterTemplatesByOwnership(sharedUserDatasourceTemplates, currentUser, false);
  const sharedTemplatesNotOwned = sharedTemplatesForTotalCount.filter((model) => !myTemplateIds.has(model.id));

  const industryTemplates = sharedTemplates.filter(
    (template) => template.datasource?.id === SeeqNames.LocalDatasources.Templates.Industry.DatasourceId,
  );

  return {
    myTemplates,
    sharedTemplatesNotOwned,
    industryTemplates,
    sharedTemplatesForTotalCount,
  };
};

/** Fetches a template by ID and sets it in the appropriate template store (Scaling or Tool) */
export const fetchTemplate = async (
  id: string,
  templateType: TemplateType,
  saveState = true,
  isEditing = false,
  mappings?: TemplateApplicationMappingV1[],
) => {
  try {
    const { data: template } = await sqTemplatesApi.getTemplate({ templateId: id });
    if (template?.inputFeatures) {
      template.inputFeatures = template.inputFeatures.map((input: InputFeatureV1) => {
        const updatedInput = { ...input } as InputFeatureV1 & {
          isCollapsed?: boolean;
          columnDefinition?: ColumnDefinition;
        };
        if (isEditing) {
          updatedInput.isCollapsed = true;
        }
        if (mappings) {
          const mapping = findMappingForFeature(mappings, input);
          if (mapping && mapping.columnId) {
            updatedInput.columnDefinition = { id: mapping.columnId } as unknown as ColumnDefinition;
          }
        }
        return updatedInput;
      });
    }
    if (template?.derivedFeatures) {
      template.derivedFeatures = template.derivedFeatures.map((derivedFeature: DerivedFeatureV1) => {
        const updatedDerivedFeature: DerivedFeatureWithColumnDefinition = { ...derivedFeature };
        if (mappings) {
          const mapping = findMappingForFeature(mappings, derivedFeature);
          if (mapping && mapping.columnId) {
            updatedDerivedFeature.columnDefinition = { id: mapping.columnId };
          }
        }
        return updatedDerivedFeature;
      });
    }
    if (saveState) {
      setTemplateOutput(templateType, template);
    }
    return template;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Opens the tool-pane apply form for a template. Callers switch to the apply tool before the fetch
 * resolves, so a failed fetch would otherwise leave the pane on a spinner with no template loaded.
 */
export const openToolTemplateForApply = async (templateId: string) => {
  resetTemplate(TEMPLATE_TYPES.TOOL);
  setActiveTool(TREND_TOOLS.MODEL_APPLY);
  const template = await fetchTemplate(templateId, TEMPLATE_TYPES.TOOL);
  if (!template) {
    setActiveTool(TREND_TOOLS.MODELS);
  }
};

type GqlResetTemplateDraftResult = NonNullable<GqlResetTemplateDraftMutation['resetTemplateDraft']>;
type GqlResetTemplateDraftFeature = GqlResetTemplateDraftResult['template']['derivedFeatures'][number];
type GqlResetTemplateDraftRule = GqlResetTemplateDraftFeature['rules'][number];
type GqlResetTemplateDraftRuleInput = GqlResetTemplateDraftRule['inputs'][number];
type GqlFeatureOutputType = GqlResetTemplateDraftFeature['outputType'];

/**
 * GraphQL `FeatureOutputType` includes list values that the REST derived-feature enum does not.
 * Map only the scalar members so `templateOutputFromResetDraft` typechecks without a cast.
 */
const toRestScalarOutputType = (outputType: GqlFeatureOutputType): DerivedFeatureOutputTypeEnum => {
  switch (outputType) {
    case 'TEXT':
      return DerivedFeatureOutputTypeEnum.TEXT;
    case 'NUMERIC':
      return DerivedFeatureOutputTypeEnum.NUMERIC;
    case 'ITEM_ID':
      return DerivedFeatureOutputTypeEnum.ITEMID;
    case 'BOOLEAN':
      return DerivedFeatureOutputTypeEnum.BOOLEAN;
    case 'TEXT_LIST':
    case 'NUMERIC_LIST':
    case 'ITEM_ID_LIST':
      throw new Error(
        `GraphQL feature output type ${outputType} is a list type and cannot be represented on the REST scalar feature contract`,
      );
  }
};

const toTemplateRuleInput = (input: GqlResetTemplateDraftRuleInput): TemplateRuleInputV1Object => ({
  id: input.id,
  name: input.name,
  description: input.description,
  type: input.type,
  inputFeatureId: input.inputFeatureId,
  argument: input.argument,
});

const toFeatureRule = (rule: GqlResetTemplateDraftRule): FeatureRuleV1 => {
  const inputs = rule.inputs.map(toTemplateRuleInput);
  switch (rule.__typename) {
    case 'TemplateBooleanConstantRule':
      return { id: rule.id, inputs, booleanConstant: { staticValue: rule.staticValue } };
    case 'TemplateFormulaCreatorRule':
      return {
        id: rule.id,
        inputs,
        formulaCreator: {
          formula: toTemplateRuleInput(rule.formula),
          parameters: toTemplateRuleInput(rule.parameters),
          name: toTemplateRuleInput(rule.name),
          description: rule.description && toTemplateRuleInput(rule.description),
          scopedTo: rule.scopedTo && toTemplateRuleInput(rule.scopedTo),
          identityId: rule.identityId && toTemplateRuleInput(rule.identityId),
          permissionString: rule.permissionString && toTemplateRuleInput(rule.permissionString),
          variableParameters: rule.variableParameters && toTemplateRuleInput(rule.variableParameters),
          variableParameterStrings: rule.variableParameterStrings && toTemplateRuleInput(rule.variableParameterStrings),
        },
      };
    case 'TemplateNumericScalarCreatorRule':
      return {
        id: rule.id,
        inputs,
        numericScalarCreator: {
          name: toTemplateRuleInput(rule.name),
          description: rule.description && toTemplateRuleInput(rule.description),
          scopedTo: rule.scopedTo && toTemplateRuleInput(rule.scopedTo),
          identityId: rule.identityId && toTemplateRuleInput(rule.identityId),
          permissionString: rule.permissionString && toTemplateRuleInput(rule.permissionString),
        },
      };
    case 'TemplateStringScalarCreatorRule':
      return {
        id: rule.id,
        inputs,
        stringScalarCreator: {
          name: toTemplateRuleInput(rule.name),
          description: rule.description && toTemplateRuleInput(rule.description),
          scopedTo: rule.scopedTo && toTemplateRuleInput(rule.scopedTo),
          identityId: rule.identityId && toTemplateRuleInput(rule.identityId),
          permissionString: rule.permissionString && toTemplateRuleInput(rule.permissionString),
        },
      };
  }
};

/**
 * Builds the `TemplateOutputV1` for a draft from a `ResetTemplateDraft` mutation response, so callers
 * don't need a follow-up `fetchTemplate` REST call just to get the draft's post-reset content. Input
 * features come back collapsed, matching `fetchTemplate`'s `isEditing` behavior, since this is only
 * ever used while editing a draft.
 */
export const templateOutputFromResetDraft = (
  templateId: string,
  draftVersionId: string,
  resetTemplateDraft: GqlResetTemplateDraftResult,
): TemplateOutputV1 => {
  const { template } = resetTemplateDraft;
  return {
    id: templateId,
    versionId: draftVersionId,
    type: SeeqNames.Types.Model,
    name: template.name,
    description: template.description,
    scopedTo: template.scopedTo,
    inputFeatures: template.inputFeatures.map((feature) => ({
      id: feature.id,
      canonicalId: feature.canonicalId,
      name: feature.name,
      description: feature.description,
      outputType: feature.outputType,
      required: feature.isRequired,
      outputItemType: feature.outputItemType,
      outputItemMetadata: feature.outputItemMetadata,
      isCollapsed: true,
    })),
    derivedFeatures: template.derivedFeatures.map((feature) => ({
      id: feature.id,
      canonicalId: feature.canonicalId,
      name: feature.name,
      description: feature.description,
      outputType: toRestScalarOutputType(feature.outputType),
      isOutput: feature.isOutput,
      outputItemType: feature.outputItemType,
      outputItemMetadata: feature.outputItemMetadata,
      rules: feature.rules.map(toFeatureRule),
    })),
  };
};

/** Canonical IDs of every input and derived feature on a template, e.g. for `editBaseFeatureCanonicalIds`. */
export const getTemplateFeatureCanonicalIds = (template: TemplateOutputV1): string[] =>
  [...(template.inputFeatures ?? []), ...(template.derivedFeatures ?? [])]
    .map((feature) => feature.canonicalId)
    .filter((id): id is string => !!id);

/** Content fields compared to detect whether a draft differs from a baseline snapshot, e.g. to disable
 *  "Reset draft" when there is nothing to discard. UI-only fields (collapse state, item-mapping
 *  selections, etc.) are deliberately excluded so toggling them doesn't look like an edit. */
interface ComparableFeatureContent {
  id?: string;
  canonicalId?: string;
  name?: string;
  description?: string;
  outputType?: unknown;
  isOutput?: boolean;
  rules?: unknown;
}

const toComparableFeature = (feature: ComparableFeatureContent): ComparableFeatureContent => ({
  id: feature.id,
  canonicalId: feature.canonicalId,
  name: feature.name,
  description: feature.description,
  outputType: feature.outputType,
  isOutput: feature.isOutput,
  rules: feature.rules,
});

export interface DraftContentSnapshot {
  name: string;
  description: string;
  derivedFeatures: ComparableFeatureContent[];
  inputFeatures: ComparableFeatureContent[];
}

/** Snapshot of a draft's edited content, taken from the live editor state. Diff two snapshots with
 *  `lodash.isEqual` to check whether the draft has changed. */
export const snapshotDraftContent = (
  name: string,
  description: string,
  existingDerivedFeatures: TemplateDerivedFeature[],
  existingInputFeatures: TemplateInput[],
): DraftContentSnapshot => ({
  name,
  description,
  derivedFeatures: existingDerivedFeatures.map(toComparableFeature),
  inputFeatures: existingInputFeatures.map(toComparableFeature),
});

/** Snapshot of the same content, taken from a `TemplateOutputV1` (e.g. right after a reset) instead of
 *  the editor state, so a fresh baseline is available immediately without waiting on a re-render. */
export const snapshotDraftContentFromTemplateOutput = (template: TemplateOutputV1): DraftContentSnapshot => ({
  name: template.name,
  description: template.description ?? '',
  derivedFeatures: (template.derivedFeatures ?? []).map(toComparableFeature),
  inputFeatures: (template.inputFeatures ?? [])
    .filter((feature) => !isSystemInputFeature(feature.name))
    .map(toComparableFeature),
});

export const findUniqueColumnNames = (tableColumnNames: string[], oldColumnNames: string[]) => {
  const newColumnNames: string[] = [];
  const duplicateOutputNames = getDuplicateOutputNames(tableColumnNames, oldColumnNames);
  oldColumnNames.forEach((name) => {
    if (duplicateOutputNames.find((duplicatedName) => duplicatedName === name)) {
      const prefix = `${name}`;
      newColumnNames.push(getNextName([...tableColumnNames, ...newColumnNames], prefix, true, true));
    } else {
      newColumnNames.push(name);
    }
  });

  return newColumnNames;
};

/**
 * Resolves duplicate column names among derived features, respecting priority:
 * 1. Existing table column names (never changed, always take precedence)
 * 2. Output features keep their name over intermediates
 * 3. Intermediate features are renamed if they conflict
 * Within the same priority level, first occurrence keeps the name.
 *
 * @param tableColumnNames - Existing column names in the table
 * @param derivedFeatures - Derived features from the template with name and isOutput properties
 * @returns Array of unique column names in the same order as derivedFeatures
 */
export const findUniqueColumnNamesWithPriority = (
  tableColumnNames: string[],
  derivedFeatures: Pick<ReferencedOutputItemV1, 'name' | 'isOutput'>[],
): string[] => {
  const outputIndices = derivedFeatures
    .map((f, i) => ({ feature: f, originalIndex: i }))
    .filter(({ feature }) => feature.isOutput);
  const intermediateIndices = derivedFeatures
    .map((f, i) => ({ feature: f, originalIndex: i }))
    .filter(({ feature }) => !feature.isOutput);

  const processingOrder = [...outputIndices, ...intermediateIndices];
  const takenNames = [...tableColumnNames];
  const result: string[] = new Array(derivedFeatures.length);

  for (const { feature, originalIndex } of processingOrder) {
    if (takenNames.includes(feature.name)) {
      const uniqueName = getNextName(takenNames, feature.name, true, true);
      result[originalIndex] = uniqueName;
      takenNames.push(uniqueName);
    } else {
      result[originalIndex] = feature.name;
      takenNames.push(feature.name);
    }
  }

  return result;
};

export const updateDerivedFeature = (
  patch: Partial<TemplateDerivedFeature>,
  index: number,
  existingDerivedFeatures: TemplateDerivedFeature[],
  templateType: TemplateType,
) => {
  const updatedFeature: TemplateDerivedFeature = {
    ...existingDerivedFeatures[index],
    ...patch,
  };
  const updatedExistingDerivedFeatures = existingDerivedFeatures.map((feature, i) =>
    i === index ? updatedFeature : feature,
  );
  setTemplate(templateType, { existingDerivedFeatures: updatedExistingDerivedFeatures });
};

export const hasCollapsedInputs = (inputs: TemplateInput[] | NewlyAddedInputFeature[]): boolean =>
  inputs.some((i) => !i.isCollapsed);

export const hasExpandedInputs = (inputs: TemplateInput[] | NewlyAddedInputFeature[]): boolean =>
  inputs.some((i) => i.isCollapsed);

const updateInputFeaturesCollapse = (inputs: TemplateInput[], collapsed: boolean, templateType: TemplateType) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setTemplate(templateType, { inputFeatures: updated });
};

const updateExistingInputFeaturesCollapse = (
  inputs: TemplateInput[],
  collapsed: boolean,
  templateType: TemplateType,
) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setTemplate(templateType, { existingInputFeatures: updated });
};

const updateNewInputsCollapse = (inputs: NewlyAddedInputFeature[], collapsed: boolean, templateType: TemplateType) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setTemplate(templateType, { newInputFeatures: updated });
};

export const expandAllLegacyInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateInputFeaturesCollapse(inputs, false, templateType);

export const collapseAllLegacyInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateInputFeaturesCollapse(inputs, true, templateType);

export const expandAllExistingInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateExistingInputFeaturesCollapse(inputs, false, templateType);

export const collapseAllExistingInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateExistingInputFeaturesCollapse(inputs, true, templateType);

export const expandAllNewInputs = (inputs: NewlyAddedInputFeature[], templateType: TemplateType) =>
  updateNewInputsCollapse(inputs, false, templateType);

export const collapseAllNewInputs = (inputs: NewlyAddedInputFeature[], templateType: TemplateType) =>
  updateNewInputsCollapse(inputs, true, templateType);

/**
 * Gets the display name for a rule parameter by finding its matching input or derived feature.
 */
const getParameterDisplayName = (
  input: TemplateRuleInputV1Object,
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): string => {
  const matchingInputFeature = existingInputFeatures.find((f) => f.id === input.inputFeatureId);
  if (matchingInputFeature) {
    // Existing features always use their feature name
    if (matchingInputFeature.canonicalId) {
      return matchingInputFeature.name;
    }
    return matchingInputFeature.name ?? input.name;
  }

  const matchingDerivedFeature = existingDerivedFeatures.find((f) => f.id === input.inputFeatureId);
  if (matchingDerivedFeature) {
    return matchingDerivedFeature.name;
  }

  return input.name;
};

/**
 * Extracts formula and parameters from a feature rule for display.
 */
const extractFormulaFromRule = (
  rule: TemplateDerivedFeature['rules'][number],
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): { formula: string; parameters: { name: string; identifier: string; item?: { id?: string } }[] } => {
  const formula = String(rule?.formulaCreator?.formula?.argument ?? '');
  const paramArgs = Array.isArray(rule?.formulaCreator?.parameters?.argument)
    ? rule.formulaCreator.parameters.argument
    : [];
  const inputs = Array.isArray(rule.inputs) ? rule.inputs : [];

  const parameters = inputs.map((input: TemplateRuleInputV1Object, idx: number) => {
    const matchingInputFeature = existingInputFeatures.find((f) => f.id === input.inputFeatureId);
    return {
      name: getParameterDisplayName(input, existingInputFeatures, existingDerivedFeatures),
      identifier: paramArgs[idx] ?? '',
      item: matchingInputFeature ? { id: matchingInputFeature.inputItem } : undefined,
    };
  });

  return { formula, parameters };
};

/**
 * Displays existing template features (TemplateDerivedFeature) as options for the FormulaItemTable.
 *
 * @param existingDerivedFeatures - Existing derived features from a template
 * @param isOutput - True to show outputs, false to show intermediates
 * @param existingInputFeatures - Model input features for parameter name resolution
 * @returns Options for the FormulaItemTable
 */
export const displayExistingTemplateFeatures = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  isOutput: boolean,
  existingInputFeatures: TemplateInput[],
): FormulaItemTableOption[] =>
  existingDerivedFeatures
    .filter((feature) => feature.isOutput === isOutput && feature.id !== undefined)
    .map((feature) => ({
      key: feature.id ?? feature.name,
      display: feature.name,
      allFormulas: (feature.rules ?? []).map((rule) =>
        extractFormulaFromRule(rule, existingInputFeatures, existingDerivedFeatures),
      ),
      outputItemType: feature.outputItemType,
      outputItemMetadata: feature.outputItemMetadata,
    }));

/**
 * Converts the features of a template into options to be display in the Formula Item Table
 *
 * @param {DerivedFeatureV1[]} features - The features to convert to options
 * @param {boolean} isOutput - True if the features are outputs, false if intermediates
 * @param {TemplateInput[]} inputFeatures - Model input features
 * @param {ColumnDefinition[]} tableColumns - Optional table columns
 * @param {boolean} useIdForKey - True to use the ID as the option key, false to use the name
 * @return {FormulaItemTableOption[]} The options for the formula item table
 */
export const displayTemplateFeatures = (
  features: DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[],
  isOutput: boolean,
  inputFeatures: TemplateInput[],
  tableColumns?: ColumnDefinition[],
  useIdForKey: boolean | undefined = false,
): FormulaItemTableOption[] =>
  features
    .filter(
      (feature: DerivedFeatureWithColumnDefinition) =>
        feature.isOutput === isOutput &&
        feature.id !== undefined &&
        (!('displayName' in feature) ||
          'showInIntermediates' in feature ||
          'featureId' in feature ||
          'columnDefinition' in feature),
    )
    .map((f: DerivedFeatureWithColumnDefinition) => {
      const allFormulas = (f.rules ?? []).map((rule) => {
        let formula = '';
        let parameters: { name: string; identifier: string; item?: { id?: string } }[] = [];

        if (
          rule &&
          typeof rule === 'object' &&
          'arguments' in rule &&
          rule.arguments &&
          typeof rule.arguments === 'object' &&
          'formula' in rule.arguments &&
          'parameters' in rule.arguments &&
          'inputs' in rule
        ) {
          formula = (rule.arguments.formula as string) ?? '';

          const paramStr = rule.arguments.parameters as string;
          const paramArray = paramStr ? paramStr.split('|') : [];
          const ruleInputs: string[] = Array.isArray(rule.inputs)
            ? rule.inputs.map((input) => (typeof input === 'string' ? input : ''))
            : [];

          parameters = ruleInputs.map((input: string, idx: number) => ({
            name: tableColumns ? tableColumns.find((col) => col.id === input)?.displayName || input : input,
            identifier: paramArray[idx] ?? '',
            item: undefined,
          }));
        } else if (rule?.formulaCreator?.formula?.argument) {
          formula = String(rule.formulaCreator.formula.argument);

          const paramArgs = Array.isArray(rule.formulaCreator.parameters?.argument)
            ? rule.formulaCreator.parameters.argument
            : [];
          const inputs = Array.isArray(rule.inputs) ? rule.inputs : [];

          parameters = inputs.map((input: TemplateRuleInputV1Object, idx: number) => {
            const matchingInputFeature = inputFeatures
              ? inputFeatures.find((feature) => feature.id === input.inputFeatureId)
              : undefined;
            return {
              name: matchingInputFeature?.name ?? input.name,
              identifier: paramArgs[idx] ?? '',
              item: matchingInputFeature ? { id: matchingInputFeature.inputItem } : undefined,
            };
          });
        }
        return { formula, parameters };
      });

      let displayName = 'columnName' in f ? String(f.columnName) : String(f.name);

      return {
        key: useIdForKey ? (f.id ?? '') : f.name,
        display: displayName,
        allFormulas,
        outputItemType: f.outputItemType,
        outputItemMetadata: f.outputItemMetadata,
      };
    });

/** Validates a formula and its parameters for a one off template application.
 *
 * @param {string} formula - the formula to validate
 * @param {Array<{name: string; identifier: string; item?: {id?: string}}>} parameters - the parameters used in the formula
 */
export const validateApplication = (
  formula: string,
  parameters: { name: string; identifier: string; item?: { id?: string } }[],
) => {
  // If the ID is missing, this is for an intermediate and there is no real item yet
  const missingId = parameters.some((param) => !param.item?.id);
  if (missingId) {
    return { formula, parameters };
  }
  return validateFormula(formula, parameters as unknown as FormulaEditorParam[])
    .then(() => {
      return { formula, parameters };
    })
    .catch((formulaErrors) => {
      return { formula, parameters, formulaErrors };
    });
};

/** Validates all formulas in all options of a Formula Item Table. If any fallback formulas are valid, clears errors
 * for that option. Returns a new array with validated options.
 * @param {FormulaItemTableOption[]} options - the formula item table options to validate
 * @returns {Promise<{ validatedOptions: FormulaItemTableOption[]; hasErrors: boolean }>} Validated options and error flag
 */
export async function validateAllOptionsFormulas(
  options: FormulaItemTableOption[],
): Promise<{ validatedOptions: FormulaItemTableOption[]; hasErrors: boolean }> {
  let formulaErrorsExist = false;

  const validatedOptions = await Promise.all(
    options.map(async (option) => {
      let optionHasErrors = false;
      let validatedFormulas = option.allFormulas;

      if (option.allFormulas && Array.isArray(option.allFormulas)) {
        const newFormulas: typeof option.allFormulas = [];
        let foundValid = false;

        // Process formulas sequentially to short-circuit on first valid fallback
        for (const formula of option.allFormulas) {
          if (foundValid) {
            // Keep remaining formulas unchanged after finding valid one
            newFormulas.push(formula);
          } else {
            try {
              const result = await exposedForTesting.validateApplication(formula.formula, formula.parameters);
              if ('formulaErrors' in result && result.formulaErrors) {
                newFormulas.push({ ...formula, formulaErrors: result.formulaErrors });
                optionHasErrors = true;
              } else {
                // Valid formula found - clear all errors and mark to skip validation of remaining
                newFormulas.push({ ...formula, formulaErrors: undefined });
                optionHasErrors = false;
                foundValid = true;
              }
            } catch (error) {
              optionHasErrors = true;
              errorToast({ httpResponseOrError: error });
              newFormulas.push(formula);
            }
          }
        }

        // If a valid fallback was found, clear all errors
        if (!optionHasErrors && newFormulas.length > 0) {
          validatedFormulas = newFormulas.map((formula) => ({ ...formula, formulaErrors: undefined }));
        } else {
          validatedFormulas = newFormulas;
        }
      }

      if (optionHasErrors) {
        formulaErrorsExist = true;
      }

      return {
        ...option,
        allFormulas: validatedFormulas,
      };
    }),
  );

  return { validatedOptions, hasErrors: formulaErrorsExist };
}

/** Updates a specific input in the template's input features list
 *
 * @param patch - the fields to update in the input
 * @param input - the input to update
 * @param templateType - the type of template being updated (Scaling or Tool)
 */
export const updateInput = (patch: Partial<TemplateInput>, input: TemplateInput, templateType: TemplateType) => {
  const idToUse = input.referenceId ?? input.id;
  if (!idToUse) {
    return;
  }
  // Makes sure we are using the most up-to-date inputs from the store
  const inputFeatures = getTemplateInputFeatures(templateType);

  let updated = false;
  const updatedFeatures = inputFeatures.map((input) => {
    const inputId = input.referenceId ?? input.id;
    if (!updated && inputId === idToUse) {
      updated = true;
      return { ...input, ...patch };
    }
    return input;
  });

  setTemplate(templateType, { inputFeatures: updatedFeatures });
};

export const renameToUniqueDerivedFeatureNames = (columns: ColumnDefinition[], templateType: TemplateType) => {
  const existingDerivedFeatures = _.cloneDeep(sqScalingTemplateStore.getState().existingDerivedFeatures);
  const newColumnNames = findUniqueColumnNamesWithPriority(
    columns.map((column) => column.displayName),
    existingDerivedFeatures,
  );
  newColumnNames.forEach((newName, index) => {
    existingDerivedFeatures[index].name = newName;
  });

  setTemplate(templateType, { existingDerivedFeatures });
};

/** Validates that none of the derived feature names already exist in the scaling table column names.
 * If any do, throws an error listing the duplicates.
 *
 * @param scalingTableColumnNames - the existing column names in the scaling table
 * @param derivedFeatureNames - the names of the derived features in the template
 */
export const validateTemplateOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = getDuplicateOutputNames(scalingTableColumnNames, derivedFeatureNames);
  if (outputNamesInTable.length) {
    const listOfNames = outputNamesInTable.map((name) => `"${name}"`).join(', ');
    const namesPlural = outputNamesInTable.length > 1 ? 'names' : 'name';
    const colsPlural = outputNamesInTable.length > 1 ? 'columns' : 'column';

    throw new Error(
      `Applying this template would create duplicate column ${namesPlural}: ${listOfNames}. ` +
        `Please rename the conflicting table ${colsPlural} and try again.`,
    );
  }
};

/**
 * Returns a list of duplicate output names that exist in both the scaling table column names and the derived feature names.
 *
 * @param {string[]} scalingTableColumnNames - The existing column names in the scaling table.
 * @param {string[]} derivedFeatureNames - The names of the derived features in the template.
 * @returns {string[]} An array of duplicate output names found in both lists.
 */
export const getDuplicateOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = Array.from(
    new Set(derivedFeatureNames.filter((name) => scalingTableColumnNames.includes(name))),
  );

  return [...outputNamesInTable];
};

/**
 * Maps a trend-store item to the wire-format `outputItemType` and `outputItemMetadata` carried on a
 * `DerivedFeatureV1`. Mirrors the backend's `FeatureOutputItemMetadata.toMetadataMap` (see
 * `appserver/items/.../Model.kt`):
 *   - Series  -> "Signal"   + `{ [ValueUom]: <uom> }`
 *   - Scalar  -> "Scalar"   + `{ [Uom]: <uom> }`
 *   - Condition -> "Condition" + `{}`
 *
 * A `valueUnitOfMeasure` of `undefined` omits the UOM key entirely (Any-UOM opt-out); an empty
 * string is preserved as a present-but-empty value (dimensionless).
 */
export const toOutputItemMetadata = (item: {
  itemType: ITEM_TYPES;
  valueUnitOfMeasure?: string;
}): OutputItemMetadataFields => {
  const uomEntry = (key: string): Record<string, string> =>
    item.valueUnitOfMeasure === undefined ? {} : { [key]: item.valueUnitOfMeasure };
  switch (item.itemType) {
    case ITEM_TYPES.SERIES:
      return {
        outputItemType: SeeqNames.FormulaType.Signal,
        outputItemMetadata: uomEntry(SeeqNames.Properties.ValueUom),
      };
    case ITEM_TYPES.SCALAR:
      return {
        outputItemType: SeeqNames.FormulaType.Scalar,
        outputItemMetadata: uomEntry(SeeqNames.Properties.Uom),
      };
    case ITEM_TYPES.CONDITION:
      return { outputItemType: SeeqNames.FormulaType.Condition, outputItemMetadata: {} };
    default:
      return {};
  }
};

/** Maps a pending picker output to a `SelectableOutput` ready for save, stamping any
 *  confirm-time-resolved metadata onto the result. */
export const toSelectableOutput = (
  output: TemplateOutputSelectValue,
  metadata?: OutputItemMetadataFields,
): SelectableOutput => ({
  ...output,
  ...metadata,
  isOutput: true,
  rules: [],
});

/** Fetches all items from the details pane of the supported types, decorates them, and returns the calculated
 *  outputs */
export const getAllDetailsPaneOutputs = (): NewlyAddedDerivedFeature[] => {
  const items = getAllItems({
    excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
    itemTypes: [ITEM_TYPES.CONDITION, ITEM_TYPES.SERIES, ITEM_TYPES.SCALAR],
  }).filter((item) => item.calculationType !== undefined && item.calculationType !== 'periodic-condition');

  const decoratedItems = decorate({ items });
  const outputs = Array.isArray(decoratedItems) ? decoratedItems : [decoratedItems];

  return outputs.map((output) => {
    const { outputItemType, outputItemMetadata } = toOutputItemMetadata(output);
    return {
      id: output.id,
      name: output.name,
      isOutput: true,
      outputItemType,
      outputItemMetadata,
    };
  });
};

/** Filters detail pane outputs to only those not already in the pending list */
export const getNewDetailsPaneOutputs = (pendingOutputs: TemplateOutputSelectValue[]): TemplateOutputSelectValue[] => {
  const calculatedItems: TemplateOutputSelectValue[] = getAllDetailsPaneOutputs().map(({ id, name }) => ({
    id: id!,
    name,
  }));
  const existingIds = new Set(pendingOutputs.map((p) => p.id));
  return calculatedItems.filter((item) => !existingIds.has(item.id));
};

const toTemplateIntermediatesFromItems = (
  items: Array<{
    id: string;
    name: string;
    inputType: string;
  }>,
  metadataMap: Map<string, OutputItemMetadataFields>,
) =>
  items.map((item) => ({
    referenceId: item.id,
    name: item.name,
    isOutput: false,
    ...metadataMap.get(item.id),
  }));

/** Collects inputs and their parent IDs from a list of dependencies. */
export function collectInputsAndParents(dependencies: OutputDependencyItem[]): CollectedInputsAndParents {
  const parentIds = new Set<string>();
  const inputsById = new Map<
    string,
    {
      id: string;
      name: string;
      originalName: string;
      inputType: string;
      description: string;
      parent?: string;
    }
  >();

  dependencies.forEach((dep) => {
    if (!dep?.id) return;

    if (!inputsById.has(dep.id)) {
      const lastAncestorName = dep.ancestors?.length ? dep.ancestors[dep.ancestors.length - 1].name : undefined;

      inputsById.set(dep.id, {
        name: dep.name,
        originalName: dep.name,
        id: dep.id,
        inputType: dep.type,
        description: '',
        parent: lastAncestorName,
      });
    }

    dep.parameterOf?.forEach((param) => {
      if (param?.id) {
        parentIds.add(param.id);
      }
    });
  });

  return { inputs: Array.from(inputsById.values()), parentIds };
}

/** Given a set of template outputs, fetches their dependencies and finds all input and intermediate items. */
export const fetchTemplateToolInputsAndIntermediates = async (
  outputs: NewlyAddedDerivedFeature[],
  inputs: NewlyAddedInputFeature[],
) => {
  const outputIds = outputs.map((output) => output.id);
  const dependencyLists = await Promise.all(
    outputIds.map(async (outputId) => {
      const deps = await getDependencies({ id: outputId as string });
      return deps?.dependencies ?? [];
    }),
  );

  const { inputs: updatedInputs, parentIds } = collectInputsAndParents(dependencyLists.flat());

  const validInputs = updatedInputs
    .filter((input) => input?.id && !parentIds.has(input.id))
    .filter((input) => input.inputType !== SeeqNames.Types.CalculatedCondition);

  const newInputs = validInputs
    .filter((validInput) => {
      return (
        !!validInput.id &&
        !inputs.some((input) => input.referenceId === validInput.id) &&
        !outputs.some((output) => output.id === validInput.id) &&
        validInput.inputType !== SeeqNames.Types.CalculatedScalar
      );
    })
    .map((input) => {
      // Capture the input's type restriction at creation
      const outputItemType = captureInputRestrictionType(input.inputType);
      return {
        id: input.id!,
        referenceId: input.id!,
        name: input.name,
        inputType: input.inputType,
        description: input.description,
        originalName: input.originalName,
        parent: input.parent,
        isCollapsed: false,
        ...(outputItemType !== undefined && { outputItemType }),
      };
    });

  const existingInputs: NewlyAddedInputFeature[] = inputs.filter(
    (input) =>
      validInputs.some((validInput) => validInput.id === input.referenceId) &&
      !outputs.some((output) => output.id === input.referenceId),
  );

  const intermediates = updatedInputs.filter(
    (input) =>
      input.inputType !== SeeqNames.Types.StoredSignal &&
      !newInputs.some((newInput) => newInput.referenceId === input.id) &&
      !inputs.some((existingInput) => existingInput.referenceId === input.id) &&
      !outputIds.includes(input.id),
  );

  // Fetch metadata for intermediates
  const intermediateIds = intermediates.map((i) => i.id);
  const metadataMap = await fetchItemsForMetadata(intermediateIds);

  const intermediatesAsDerived: NewlyAddedDerivedFeature[] = toTemplateIntermediatesFromItems(
    intermediates,
    metadataMap,
  ).map((intermediate) => ({
    id: intermediate.referenceId!,
    referenceId: intermediate.referenceId!,
    name: intermediate.name,
    isOutput: false,
    rules: [],
    outputItemType: intermediate.outputItemType,
    outputItemMetadata: intermediate.outputItemMetadata,
  }));

  return {
    newInputFeatures: [...existingInputs, ...newInputs],
    newDerivedFeatures: [...outputs, ...intermediatesAsDerived],
  };
};

export const getInputIcon = (type: string | undefined) => {
  switch (type) {
    case SeeqNames.Types.StoredCondition:
      return "<i class='fc fc-capsule-set sq-icon-color ml3'></i>";
    case SeeqNames.Types.StoredSignal:
      return "<i class='fc fc-series sq-icon-color ml3'></i>";
    case SeeqNames.Types.CalculatedScalar:
      return "<i class='fc fc-scalar sq-icon-color ml3'></i>";
    default:
      return '';
  }
};

export const getValidColumnType = (outputType: OutputTypeEnum | undefined) => {
  switch (outputType) {
    case OutputTypeEnum.ITEMID:
      return ColumnTypeEnum.UUID;
    case OutputTypeEnum.NUMERIC:
      return ColumnTypeEnum.NUMERIC;
    case OutputTypeEnum.TEXT:
      return ColumnTypeEnum.TEXT;
    default:
      return null;
  }
};

export const resetTemplateApplicationAndEdit = (editorType: EditorType) => {
  setIsApplyingTemplate(editorType, false);
  setColumnIdToBeUsedAsTemplateInput(editorType, undefined);
  setTemplateInputToSelect(editorType, undefined);
  setColumnToEditId(editorType, undefined);
  setShowColumnProperties(editorType, false);
  setSelectedColumns(editorType, []);
};

/** Adds a newly created input column to the scaling table and updates the corresponding input feature in the template's
 *  input features list.
 */
export const updateNewlyCreatedTemplateInput = async (
  editorType: EditorType,
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionInputV1,
  templateInputToSelect: ModelToSelect | undefined,
  existingInputFeatures: TemplateInput[],
) => {
  try {
    const output = await addOrUpdateColumn(editorType, tableDefinitionId, columnDefinition, undefined);
    if (output) {
      const colDefs = output.tableDefinition.columnDefinitions;
      const addedColumn = colDefs[colDefs?.length - 1];
      if (!addedColumn) {
        return;
      }

      setColumnIdToBeUsedAsTemplateInput(editorType, addedColumn.id);

      if (templateInputToSelect) {
        const foundColumn = colDefs.find((column: { id: string }) => column.id === addedColumn.id);
        const inputFeatureToUpdate = existingInputFeatures.find((input) => input.id === templateInputToSelect.id);

        if (foundColumn && inputFeatureToUpdate) {
          updateInputFeature(
            {
              columnDefinition: {
                ...foundColumn,
                displayProperty: SeeqNames.Properties.Name,
              } as ColumnDefinition,
            },
            inputFeatureToUpdate,
            [],
            existingInputFeatures,
            false,
            TEMPLATE_TYPES.SCALING,
          );
        }
      }

      setControlPane(editorType, CONTROL_PANE_DISPLAY.TEMPLATE_CONTROL);
    }
  } catch (err) {
    errorToast({ httpResponseOrError: err });
  }
};

/**
 * Finds the instance output for a template feature, preferring canonicalId (stable across template versions)
 * and falling back to featureId for same-version instances and rows that have no canonical id persisted.
 */
export const findInstanceOutputForFeature = (
  outputs: InstanceOutputMappingV1[],
  feature: { id?: string; canonicalId?: string },
): InstanceOutputMappingV1 | undefined => {
  if (feature.canonicalId !== undefined) {
    const byCanonicalId = outputs.find((output) => output.featureCanonicalId === feature.canonicalId);
    if (byCanonicalId) {
      return byCanonicalId;
    }
  }
  if (feature.id !== undefined) {
    return outputs.find((output) => output.featureId === feature.id);
  }
  return undefined;
};

/**
 * Canonical IDs of the outputs already computed for the instance.
 */
export const getInstanceOutputCanonicalIds = (
  template: TemplateOutputV1,
  templateInstance: InstanceOutputV1 | null,
): string[] => {
  const existingOutputs = (templateInstance?.outputs ?? []).filter((output) => !!output.outputId);
  return (template.derivedFeatures ?? [])
    .filter(
      (feature): feature is DerivedFeatureV1 & { id: string; canonicalId: string } =>
        !!feature.isOutput &&
        !!feature.id &&
        !!feature.canonicalId &&
        findInstanceOutputForFeature(existingOutputs, feature) !== undefined,
    )
    .map((feature) => feature.canonicalId);
};

/**
 * Applies a selected template once and creates the derived features as calculated items.
 */
export const applyTemplateOnce = async (modelId: string, input: TemplateInitialApplicationInputV1) => {
  const { data: modelOutput } = await sqTemplatesApi.applyTemplate(input, { templateId: modelId });
  return modelOutput;
};

/** Adds items created from applying a template to the trend. */
export const addTemplateItemsToTrend = async (
  outputs: InstanceOutputMappingV1[],
  lane?: number,
  axisAlign?: string,
) => {
  const validOutputs = outputs.filter(
    (output): output is InstanceOutputMappingV1 & { outputId: string } => !!output.outputId,
  );

  if (validOutputs.length === 0) {
    return;
  }

  const items = await Promise.all(
    validOutputs.map((output) => sqItemsApi.getItemAndAllProperties({ id: output.outputId }).then(({ data }) => data)),
  );

  // Get instanceId once from the first item — all outputs share the same instance
  const instanceId = getTemplateInstanceIdFromProps(items.find((item) => item?.properties)?.properties);

  if (instanceId) {
    // Get template props once, reuse for all
    const props = await getTemplateProps(instanceId);
    await Promise.all(
      items
        .filter(Boolean)
        .map((item) => addTrendItem({ ...item, lane, axisAlign } as ItemOutputWithLaneAndAxis, props)),
    );
  }
};

export const getInstanceById = async (instanceId: string): Promise<InstanceOutputV1> => {
  const { data: instance } = await sqTemplatesApi.getInstance({ instanceId });
  return instance;
};

export const reapplyTemplate = async (modelId: string, instanceId: string, input: TemplateReapplicationInputV1) => {
  const { data: modelOutput } = await sqTemplatesApi.reapplyTemplate(input, { templateId: modelId, instanceId });
  return modelOutput;
};

/**
 * Applies or reapplies a template using the provided input mappings and adds the
 * resulting outputs to the details pane. Resolves with the response once the outputs are on the trend, or undefined
 * when the apply failed or returned nothing, so the caller decides whether to tear down. A reapply still resolves
 * with its response when its outputs can't be added to the trend, because the server has already retired and
 * replaced items and the caller needs the response to report them.
 *
 * On reapply, clears retired output metadata and evicts the cached instance so a subsequent edit of this
 * instance doesn't seed from a stale snapshot.
 */
export const executeSingleTemplateApplication = async (
  modelId: string,
  inputFeatures: TemplateInput[],
  workbookId: string,
  instanceToEditId: string | undefined,
  instanceName: string,
  optionalInputIds: Set<string>,
  selectedOutputCanonicalIds: string[],
  queryClient: QueryClient,
): Promise<InstanceOutputV1 | undefined> => {
  try {
    const inputMappings = createInputMappings({
      inputFeatures,
      optionalInputIds,
      isMapped: (input) => !!input.inputItem,
      instanceName,
      getInputMapping: (input) => ({ itemIdInput: input.inputItem }),
    });

    if (!instanceToEditId) {
      const result = await exposedForTesting.applyTemplateOnce(modelId, {
        inputs: inputMappings,
        sourceId: workbookId,
        datasourceId: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
        datasourceClass: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
        selectedOutputCanonicalIds,
      } as TemplateInitialApplicationInputV1);

      if (!result) {
        return undefined;
      }

      const outputs = result.outputs ?? [];
      const created = outputs.filter((output) => output.outputId).length;
      await exposedForTesting.addTemplateItemsToTrend(outputs);
      successToast({
        messageKey: 'TEMPLATES.APPLY_ONCE_MESSAGE_WITH_COUNT',
        messageParams: { created, count: selectedOutputCanonicalIds.length || outputs.length },
      });
      return result;
    } else {
      // Fetch current instance outputs before reapplying so we can detect which items got retired
      const previousInstance = await sqTemplatesApi.getInstance({ instanceId: instanceToEditId });
      const previousOutputs = previousInstance.data.outputs ?? [];

      const result = await exposedForTesting.reapplyTemplate(modelId, instanceToEditId, {
        inputs: inputMappings,
        sourceId: workbookId,
        datasourceId: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
        datasourceClass: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
        selectedOutputCanonicalIds,
      } as TemplateReapplicationInputV1);

      computeRetiredOutputItemIds(previousOutputs, result.outputs ?? []).forEach((itemId) =>
        clearTemplateProps(itemId),
      );

      try {
        await exposedForTesting.addTemplateItemsToTrend(result.outputs ?? []);
      } catch (e) {
        errorToast({ httpResponseOrError: e });
      }
      evictInstanceQuery(queryClient, instanceToEditId);
      successToast({ messageKey: 'TEMPLATES.REAPPLY_MESSAGE' });
      return result;
    }
  } catch (e) {
    if (isForbidden(e)) {
      errorToast({ messageKey: 'TEMPLATES.REAPPLY_NO_PERMISSION' });
    } else {
      errorToast({ httpResponseOrError: e });
    }
    return undefined;
  }
};

/** Retires the instance on the backend and clears out local trend properties related to templates */
export const retireInstance = async (instanceId: string, templateType: TemplateType): Promise<void> => {
  let instance;
  try {
    instance = (await sqTemplatesApi.getInstance({ instanceId })).data;
    await sqTemplatesApi.retireInstance({ instanceId });
  } catch (e) {
    if (isForbidden(e)) {
      errorToast({ messageKey: 'TEMPLATES.RETIRE_NO_PERMISSION' });
    } else {
      errorToast({ httpResponseOrError: e });
    }
    return;
  }
  if (instance.outputs) {
    const itemsPromises = instance.outputs
      .map((output) => {
        if (output.outputId) {
          return sqItemsApi.getItemAndAllProperties({ id: output.outputId });
        }
        return undefined;
      })
      .filter((promise) => !!promise);
    const itemsData = await Promise.all(itemsPromises);
    itemsData.forEach((itemData) => {
      if (itemData) {
        clearTemplateProps(itemData.data.id);
      }
    });
  }
  resetTemplate(templateType);
};

/** Retires a template table instance */
export const retireTableInstance = async (
  applicationId: string,
  templateType: TemplateType,
  shouldDeleteColumns = false,
): Promise<void> => {
  await sqTemplatesApi.retireApplication({ applicationId, deleteColumns: shouldDeleteColumns });
  resetTemplate(templateType);
  await clearRetiredTemplatesProps({ onlyScalingItems: true });
};

export const retireTableInstanceAndResetTable = async (
  applicationId: string | undefined,
  tableDefinitionId: string,
  queryClient: QueryClient,
  templateId: string,
  shouldDeleteColumns?: boolean,
) => {
  const editorType = EDITOR_TYPES.SCALING;
  const templateType = TEMPLATE_TYPES.SCALING;
  if (applicationId) {
    try {
      await exposedForTesting.retireTableInstance(applicationId, templateType, shouldDeleteColumns);
    } catch (e) {
      if (isForbidden(e)) {
        errorToast({ messageKey: 'TEMPLATES.RETIRE_NO_PERMISSION' });
      } else {
        errorToast({ httpResponseOrError: e });
      }
      return;
    }
    await getExistingTableDefinition(tableDefinitionId, editorType);
    await invalidateTableTemplate(queryClient, templateId);

    setDoTableReload(editorType, true);
    setTemplateInputToSelect(editorType, undefined);
    setIsApplyingTemplate(editorType, false);
  }
};

/**
 * Given a list of template applications, fetches all input features and returns their mapped column IDs, keyed by
 * the (normalized) id of the application that consumes them as an input.
 *
 * Keyed per-application because the same column can be an output of one template application and an input of
 * another (e.g. a Scaling Table's output column is fed as an input into a second, later-applied template) — a flat
 * list would incorrectly mark that column as "input-only" everywhere, stripping its template-output status from
 * the application that actually produces it.
 */
export const fetchInputFeaturesFromTableApplication = async (
  templateColumns: TemplateTableApplicationsOutputListV1,
): Promise<Record<string, string[]>> => {
  const applications = templateColumns?.applications ?? [];
  const idsByApplication: Record<string, string[]> = {};
  for (const application of applications) {
    const applicationKey = getTemplateApplicationKey(application);
    if (applicationKey) {
      const template = await fetchTemplate(application.templateGuid!, TEMPLATE_TYPES.SCALING, false);
      const ids = new Set<string>();
      if (template?.inputFeatures) {
        template.inputFeatures.forEach((feature) => {
          const mapping = findMappingForFeature(application.mappings!, feature);
          if (mapping?.columnId) {
            ids.add(mapping.columnId);
          }
        });
      }
      idsByApplication[applicationKey] = Array.from(ids);
    }
  }
  return idsByApplication;
};

/**
 * Given a list of column definitions and a list of template applications, returns a new list of column definitions
 * where columns that are outputs of a template application are grouped under that template's name.
 *
 * @param agColumnDefs - The list of column definitions to group
 * @param applications - The list of template applications to use for grouping
 * @param inputFeatureColumnIds - Column IDs that are input features, keyed by application id (these should not
 *   be grouped under the application that consumes them as an input)
 * @param versionByModelGuid - When supplied, attaches the TemplateColumnHeader as the column-group's
 *   headerGroupComponent so each template group can render version metadata (e.g. "v{N}").
 * @param editingTemplate - Template container for the application being edited in the template
 * @param editingTemplatePreviewColumnIds - Preview column ids for new outputs selected during template edit
 * @param onUpgradeApplication - When supplied alongside `versionByModelGuid`, the column-group header
 *   renders a clickable upgrade badge for outdated applications and invokes this callback with the
 *   matching application.
 * @returns A new list of column definitions with template output columns grouped under their respective templates
 */
export interface EditingTemplateApplicationIds {
  modelContainerId?: string;
}

/** First table application for the template container being edited (version may differ while editing a draft). */
export const findEditingApplicationIndexForPreview = (
  applications: TemplateTableApplicationsOutputV1[],
  editingTemplate?: EditingTemplateApplicationIds,
): number => {
  const containerId = normalizeId(editingTemplate?.modelContainerId);
  if (!containerId) {
    return -1;
  }

  // TODO: CRAB-66235: Uses only the first application of the container; a table can have several.
  return applications.findIndex((app) => normalizeId(app.templateContainerId) === containerId);
};

export function getTemplateColumnsDefs(
  agColumnDefs: ColDef[],
  applications: TemplateTableApplicationsOutputV1[],
  inputFeatureColumnIds: Record<string, string[]> | undefined,
  versionByModelGuid?: Map<string, ApplicationVersionInfo>,
  editingTemplate?: EditingTemplateApplicationIds,
  editingTemplatePreviewColumnIds?: string[],
  onUpgradeApplication?: (application: TemplateTableApplicationsOutputV1) => void,
): (ColDef | ColGroupDef)[] {
  const editingApplicationIndex = findEditingApplicationIndexForPreview(applications, editingTemplate);

  const groups: ColGroupDef[] = applications.map((app, applicationIndex) => {
    const ownInputFeatureColumnIds = inputFeatureColumnIds?.[getTemplateApplicationKey(app) ?? ''] ?? [];
    const children = agColumnDefs.filter((colDef) => {
      const field = colDef.field as string;
      if (ownInputFeatureColumnIds.includes(field)) {
        return false;
      }
      const inMappings = app.mappings?.some((m) => m.columnId === field);
      const inEditPreview =
        applicationIndex === editingApplicationIndex &&
        editingApplicationIndex !== -1 &&
        editingTemplatePreviewColumnIds?.includes(field);
      return inMappings || inEditPreview;
    });

    const versionInfo = app.templateGuid ? versionByModelGuid?.get(app.templateGuid) : undefined;
    const onUpgrade = onUpgradeApplication ? () => onUpgradeApplication(app) : undefined;

    return {
      headerName: app.templateName,
      marryChildren: true,
      children,
      ...(versionByModelGuid && {
        headerGroupComponent: TemplateColumnHeader,
        headerGroupComponentParams: { versionInfo, onUpgrade },
      }),
    } as ColGroupDef;
  });

  const addedGroups = new Map<any, true>();
  const allColumns: (ColDef | ColGroupDef)[] = [];

  // Find the group that contains a given column field
  const findGroupForField = (field?: string) =>
    field ? groups.find((g) => (g.children as ColDef[]).some((child) => child.field === field)) : undefined;

  // Iterate through the original column definitions and add them to the new list, grouping if the column belongs to a template
  // and hasn't already been added
  agColumnDefs.forEach((colDef) => {
    const group = findGroupForField(colDef.field as string);

    if (group) {
      if (!addedGroups.has(group)) {
        addedGroups.set(group, true);
        allColumns.push(group);
      }
    } else {
      allColumns.push(colDef);
    }
  });

  return allColumns;
}

/**
 * Duplicates a given template by creating a new name, scoping it to the current workbook, and then
 * returning the result.
 *
 * @param originalTemplate - The template to duplicate (only id and name are needed)
 * @param workbookId - The ID of the workbook to scope the duplicate to
 * @returns The newly created template
 */
export const duplicateTemplate = async (
  originalTemplate: { id: string; name: string },
  workbookId: string,
): Promise<TemplateOutputV1> => {
  const duplicateName = t('TEMPLATES.DUPLICATED_TEMPLATE_NAME', { originalTemplateName: originalTemplate.name });
  const duplicateResult = await sqTemplatesApi.duplicateTemplate(
    { name: duplicateName, scopedTo: workbookId },
    { templateId: originalTemplate.id },
  );
  return duplicateResult.data;
};

/**
 * The column an input feature is mapped to within `columns`, or undefined when it isn't mapped to one of them.
 *
 * A `columnDefinition` left on an input feature is a claim, not proof. The scaling template store is persisted
 * per worksheet, so an input can still carry the column it was mapped to in a *different* Scaling Table, and a
 * reapply rehydrates whatever columns the saved mappings named -- including columns deleted since. Either way
 * the ID resolves to nothing in the table being applied to, and the mapping is not one.
 *
 * Scaling callers answer "is this mapped?" through this function rather than by reading `columnDefinition`
 * directly, so the input pickers, the output status badges and the apply request cannot disagree about it.
 */
export const resolveMappedColumn = (
  columns: ColumnDefinition[],
  input: Pick<TemplateInput, 'columnDefinition'>,
): ColumnDefinition | undefined => {
  const columnId = input.columnDefinition?.id;
  return columnId ? columns.find((column) => column.id === columnId) : undefined;
};

/**
 * Configuration for creating input mappings for template applications.
 */
export interface CreateInputMappingsConfig {
  /** The input features to map */
  inputFeatures: TemplateInput[];
  /** Input feature IDs that no selected output requires: variable parameters and fallback-branch alternatives */
  optionalInputIds: Set<string>;
  /** Function to check if an input is mapped (e.g., has an inputItem or columnDefinition) */
  isMapped: (input: TemplateInput) => boolean;
  /** Instance name to use, or undefined to fall back to input.inputText / a backend-resolved default */
  instanceName?: string;
  /** Function to extract the input mapping value (itemIdInput for tool, columnId for table) */
  getInputMapping: (input: TemplateInput) => { itemIdInput?: string; columnId?: string };
}

/**
 * Creates input mappings for template applications, handling both tool and table contexts.
 * Filters out unmapped optional inputs and handles instance name mapping.
 *
 * @param config - Configuration for creating input mappings
 * @returns Array of template application mappings
 */
export const createInputMappings = (config: CreateInputMappingsConfig): TemplateApplicationMappingV1[] => {
  const { inputFeatures, optionalInputIds, isMapped, instanceName, getInputMapping } = config;

  return inputFeatures
    .filter((input) => {
      // The scope is decided by where the template is being applied, so the UI leaves it unmapped and lets
      // the backend fill it in. Other clients are free to map it themselves.
      if (input.name === SeeqNames.Models.ScopedToInputFeatureName) return false;
      if (input.name === SeeqNames.Models.InstanceNameInputFeatureName) return true;
      // An unmapped optional input has no value to send; the backend treats a non-required input as absent
      return !(input.id && optionalInputIds.has(input.id) && !isMapped(input));
    })
    .map((input) => {
      const isInstanceName = input.name === SeeqNames.Models.InstanceNameInputFeatureName;
      const instanceNameMapping = isInstanceName ? getInputMapping(input) : undefined;
      const hasInstanceNameColumn = isInstanceName && !!instanceNameMapping?.columnId;

      let textInput: string | undefined;
      if (isInstanceName && !hasInstanceNameColumn) {
        if (instanceName !== undefined && instanceName !== '') {
          textInput = instanceName;
        } else if (input.inputText) {
          textInput = input.inputText;
        }
      }

      return {
        featureId: input.id,
        textInput,
        ...(isInstanceName ? (hasInstanceNameColumn ? instanceNameMapping : {}) : getInputMapping(input)),
      };
    });
};

export const getOutputMapping = (
  existingDerivedFeatures: TemplateDerivedFeature[],
): TemplateDerivedColumnNamesInputV1[] =>
  existingDerivedFeatures.map((feature) => ({
    columnName: feature.name,
    derivedFeatureId: feature.id!,
  }));

export const exposedForTesting = {
  validateApplication,
  applyTemplateOnce,
  reapplyTemplate,
  addTemplateItemsToTrend,
  retireTableInstance,
  createInputMappings,
  toRestScalarOutputType,
};

export type GraphQLTemplateLibraryRow =
  | GqlMyTemplatesQuery['myTemplates'][number]
  | GqlSharedTemplatesQuery['sharedTemplates'][number]
  | GqlIndustryTemplatesQuery['industryTemplates'][number];

/**
 * Maps a row from any of the three template-library queries to the ModelRowItem shape.
 * `!= null` is used (not `!== undefined`): the generated codegen types nullable fields as
 * `T | undefined`, but the runtime fetcher returns them as `null`, so `!== undefined` would
 * treat every unset field as "present" and produce bogus versionMetadata.
 */
export function modelRowItemFromGraphQLTemplate(row: GraphQLTemplateLibraryRow): ModelRowItem {
  const versionMetadata: TemplateVersionMetadata | undefined =
    row.productionVersion != null || row.draft != null
      ? {
          hasDraft: row.draft != null,
          draftVersionId: row.draft?.id,
          productionVersionNumber: row.productionVersion?.number,
          // Zero-filled: not selected by the list queries and unread by ModelRowItem consumers
          versionCount: 0,
          isLocked: row.productionVersion != null,
          versions: [],
        }
      : undefined;

  return {
    id: row.id,
    name: row.name,
    type: 'ModelContainer',
    isArchived: row.isArchived,
    canWrite: row.effectivePermissions.write,
    versionMetadata,
    isAssetGroupItem: false,
    includedProperties: {},
    parameters: [],
    ancestors: [],
    hasChildren: false,
    hasActiveChildren: false,
  };
}
